import { MouseEventHandler, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, RecurlyProvider } from '@recurly/react-recurly';
import { useHistory, useParams, Route, Switch } from 'react-router-dom';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { PageLayout } from 'components/PageLayout';
import { InlineStatus } from 'components/InlineStatus';
import { ENV } from 'shared/ENV';
import { useAuthState } from 'state/useAuthState';
import { OrganizationSeatsForm } from 'features/organizations/components/OrganizationSeatsForm';
import { OrganizationAccountInfoForm } from 'features/organizations/components/OrganizationAccountInfoForm';
import { OrganizationBillingInfoForm } from 'features/organizations/components/OrganizationBillingInfoForm';
import {
  OrganizationState,
  useOrganizationState,
} from 'features/organizations/state/useOrganizationState';
import { useOrganizationPurchase } from 'features/organizations/hooks/useOrganizationPurchase';
import { PurchaseFlowStep } from 'features/organizations/organizations.types';
import { OrganizationQuoteInfoForm } from 'features/organizations/components/OrganizationQuoteInfoForm';
import { OrganizationSuccessMessage } from 'features/organizations/components/OrganizationSuccessMessage';
import { useHandlePaymentClientToken } from 'hooks/useHandlePaymentClientToken';
import { useOrganizationQuote } from 'features/organizations/hooks/useOrganizationQuote';
import { useQueryParams } from 'hooks/useQueryParams';
import { enqueueSnackbar } from 'notistack';
import { persistedQueryClient } from 'components/CacheableQueryClientProvider';
import { Helmet } from 'react-helmet';
import { getPaywallLink } from 'features/membership/membership.utils';
import { Loader } from 'components/Loader';

type OrganizationPurchaseSuccessProps = {
  clientToken?: string;
};

const OrganizationPurchaseSuccess = ({
  clientToken,
}: OrganizationPurchaseSuccessProps): JSX.Element => {
  const { t } = useTranslation();
  const { isPending } = useHandlePaymentClientToken({
    clientToken,
  });
  const history = useHistory();

  const clearMembershipStatusAndNavigate: MouseEventHandler = async (
    event,
  ): Promise<void> => {
    event.preventDefault();
    await persistedQueryClient.invalidateQueries({
      queryKey: ['auth-user'],
    });
    history.push('/organization');
  };

  return (
    <OrganizationSuccessMessage
      headingText={t('organizationPurchase.success.title')}
      subheadingText={t('organizationPurchase.success.thanks')}
      buttonText={t('organizationPurchase.success.button')}
      onClick={clearMembershipStatusAndNavigate}
      to="/organization"
      isLoading={isPending}
    />
  );
};

export const OrganizationPurchaseFlow = (): JSX.Element => {
  const { step = 'seats' } = useParams<{ step?: PurchaseFlowStep }>();
  const {
    user,
    permissions: { canManageOrganization },
  } = useAuthState();
  const history = useHistory();
  const { t } = useTranslation();
  const [{ token }] = useQueryParams<{ token: string }>();
  const { quote, isLoading: isQuoteLoading } = useOrganizationQuote({ token });

  const {
    updateOrganizationState,
    resetOrganizationState,
    clientToken,
    ...organizationState
  } = useOrganizationState();

  const { isLoading, isError } = useOrganizationPurchase({
    processOrganizationPurchase: {
      proSeats: organizationState.proSeats,
      regularSeats: organizationState.regularSeats,
    },
  });

  const totalSeats =
    (organizationState.proSeats || 0) + (organizationState.regularSeats || 0);

  // In case the user visits this page directly to jump ahead, redirect them to the right place
  useEffect(() => {
    // if the user is already logged in not on the success page redirect to the organization page
    if (canManageOrganization && step !== 'success') {
      history.push('/organization');
    }

    switch (step) {
      case 'success':
        break;
      case 'billing':
        if (!organizationState.companyName) {
          history.push('/organization/purchase/account');
        }
        break;
      case 'account':
      case 'quote':
        if (totalSeats === 0) {
          history.push('/organization/purchase/');
        }
        break;
      default:
        break;
    }
  }, [
    canManageOrganization,
    clientToken,
    history,
    organizationState.companyName,
    totalSeats,
    step,
  ]);

  const firstName = organizationState.firstName || user?.firstName;
  const lastName = organizationState.lastName || user?.lastName;
  const email = organizationState.email || user?.email;
  useEffect(() => {
    if (step === 'seats' && totalSeats === 0) {
      updateOrganizationState({
        firstName,
        lastName,
        email,
        proSeats: 0,
        regularSeats: 0,
      });
    }
  }, [updateOrganizationState, step, totalSeats, firstName, lastName, email]);

  // restore quote from token if it exists
  useEffect(() => {
    if (!token || isQuoteLoading) {
      return;
    }

    if (!quote) {
      resetOrganizationState();
      enqueueSnackbar(t('organizationPurchase.quoteNotFound'), {
        variant: 'error',
      });
      history.replace('/organization/purchase/');
      return;
    }

    updateOrganizationState({
      companyName: quote.companyName,
      firstName: quote.firstName,
      lastName: quote.lastName,
      email: quote.email,
      proSeats: quote.proSeats,
      regularSeats: quote.regularSeats,
      poNumber: quote.poNumber,
    });
    history.replace('/organization/purchase/account');
  }, [
    history,
    isQuoteLoading,
    quote,
    resetOrganizationState,
    t,
    token,
    updateOrganizationState,
  ]);

  const handleSubmitNewStep = (
    data: Partial<OrganizationState>,
    newStep: PurchaseFlowStep,
  ): void => {
    updateOrganizationState({
      ...data,
    });
    history.push(
      `/organization/purchase/${newStep === 'seats' ? '' : newStep}`,
    );
  };

  const getHeaderAction = (): string => {
    if (step === 'seats') {
      const paywallLink = getPaywallLink({
        focusedPlan: 'teams',
        source: 'organization_purchase_page_header',
      });
      return paywallLink.url;
    }
    if (step === 'success') {
      return 'noControls';
    }
    if (step === 'account' && history.length <= 2) {
      // If we have a history length of 1 or 2 means the user likely navigated directly
      // here using a quote link - go back to the homepage
      return '/';
    }
    return 'back';
  };

  return (
    <PageLayout
      data-cname="OrganizationPurchaseFlow"
      allowAppBanner={false}
      headerBarProps={{
        title: t('organizationPurchase.title'),
        action: getHeaderAction(),
      }}
    >
      <>
        <Helmet>
          <title>{t('organizationPurchase.title')}</title>
        </Helmet>

        <SimplePageLayout>
          {isLoading || isError ? (
            <Loader disableBackground />
          ) : (
            <RecurlyProvider publicKey={ENV.VITE_RECURLY_PUBLIC_TOKEN}>
              <Elements>
                <Switch>
                  <Route exact path="/organization/purchase/">
                    <OrganizationSeatsForm
                      onSubmit={(data, nextStep?: PurchaseFlowStep) => {
                        handleSubmitNewStep(data, nextStep || 'account');
                      }}
                    />
                  </Route>
                  <Route path="/organization/purchase/quote">
                    <OrganizationQuoteInfoForm />
                  </Route>
                  <Route path="/organization/purchase/account">
                    <OrganizationAccountInfoForm
                      onSubmit={(data) => {
                        handleSubmitNewStep(data, 'billing');
                      }}
                    />
                  </Route>
                  <Route path="/organization/purchase/billing/:new?">
                    <OrganizationBillingInfoForm
                      onSubmit={(data) => {
                        handleSubmitNewStep(data, 'success');
                      }}
                    />
                  </Route>
                  <Route path="/organization/purchase/success">
                    <OrganizationPurchaseSuccess clientToken={clientToken} />
                  </Route>
                  <Route path="*">
                    <InlineStatus
                      status={isQuoteLoading || isLoading ? 'loading' : 'error'}
                    />
                  </Route>
                </Switch>
              </Elements>
            </RecurlyProvider>
          )}
        </SimplePageLayout>
      </>
    </PageLayout>
  );
};
